import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import type { TranslationKey } from '@heidi/runtime/i18n';
import { useTranslations } from '@heidi/runtime/i18n';
import { cn, getFaviconUrl } from '@heidi/utils';
import { Check } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { VList } from 'virtua';
import { buildBlockedHostSet, classifyDomainInput } from '../../../contracts';
import { useBlockedDomains } from '../../../data/query/useBlockedDomains';
import { FaviconImage } from '../FaviconImage';
import { SearchDropdownLayout } from '../hooks/useSearchDropdown';
import { useSearchDropdown } from '../hooks/useSearchDropdown.utils';
import { CustomDomainRow } from './CustomDomainRow';
import { MIN_QUERY_LENGTH, useFilteredSources } from './source-search-utils.helpers';
import { SourceSearchCapBar } from './SourceSearchCapBar';
import { SourceSearchMultiSelectControls } from './SourceSearchMultiSelectControls';
import { useCapAwareMultiSelect } from './useCapAwareMultiSelect';

export interface SourceSearchInputProps {
  /** Domains already added -- excluded from dropdown results */
  excludedDomains?: string[];
  /** Called when the user clicks a single result (or adds a custom domain). */
  onSelectSource: (domain: string) => void;
  /** When provided, list rows render checkboxes and a sticky footer offers
   *  "Select all" + "Add N sources" so users can batch-add multiple sources
   *  in one go. The custom-domain row still uses `onSelectSource`. */
  onSelectSources?: (domains: string[]) => void;
  /** Per-collection ceiling. With multi-select on, surfaces a live
   *  "{N} more can be added" counter and refuses to push pending past
   *  `maxAllowed - excludedDomains.length`. Mirrors the BE cap also
   *  re-enforced at submit via `CollectionCapDialog` so the user gets
   *  immediate feedback instead of silent post-Apply truncation. Keep
   *  in lockstep with `getMaxDomainsPerCollection()`. */
  maxAllowed?: number;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
}

export const SourceSearchInput = ({
  excludedDomains = [],
  onSelectSource,
  onSelectSources,
  maxAllowed,
  placeholder,
  autoFocus = false,
  className,
}: SourceSearchInputProps) => {
  const { t } = useTranslations();
  const effectivePlaceholder =
    placeholder ?? t('library.wizard.entry.searchPlaceholder' as TranslationKey);
  const multiSelectEnabled = !!onSelectSources;

  const { query, setQuery, isOpen, setIsOpen, containerRef, inputRef, clearAndClose } =
    useSearchDropdown();

  const { filteredResults, allSources, excludedSet } = useFilteredSources(
    query,
    excludedDomains
  );

  const { data: blockedDomains } = useBlockedDomains();
  const blockedHosts = useMemo(
    () => buildBlockedHostSet(blockedDomains ?? []),
    [blockedDomains]
  );

  const visibleDomains = useMemo(
    () => filteredResults.map((s) => s.domain),
    [filteredResults]
  );

  // `cap` is narrowed via the `multiSelectEnabled` guard so the cap
  // bar renders only in the multi-select code path (single-select
  // surfaces never had a cap mental model). The hook then owns all
  // pending-set state plus cap enforcement.
  const cap = multiSelectEnabled ? maxAllowed : undefined;
  const {
    pending,
    togglePending,
    handleSelectAllVisible,
    handleClearPending,
    resetPending,
    allVisibleSelected,
    canSelectAllVisible,
    remaining,
    showCapError,
  } = useCapAwareMultiSelect({
    visibleDomains,
    excludedCount: excludedDomains.length,
    cap,
    isOpen,
  });

  const handleSubmitPending = useCallback(() => {
    if (!onSelectSources || pending.size === 0) return;
    onSelectSources(Array.from(pending));
    resetPending();
    inputRef.current?.blur();
    clearAndClose();
  }, [onSelectSources, pending, resetPending, inputRef, clearAndClose]);

  const customDomainInfo = useMemo(() => {
    const trimmed = query.trim();
    // Gate behind the same threshold as the suggestion list — a 1- or
    // 2-character "domain" is never a real one, so offering "Add custom
    // domain" for it is misleading noise.
    if (trimmed.length < MIN_QUERY_LENGTH) return null;
    const classification = classifyDomainInput(
      query,
      excludedSet,
      allSources,
      blockedHosts
    );
    if (!classification) return null;
    // Always surface duplicates — even for non-domain-like queries
    // like "PubMed" — so the user gets explicit feedback when typing
    // the label or domain of a source already in the collection.
    // `classifyDomainInput`'s label-match path produces this case.
    // Without this branch, an excluded source filtered out of the
    // suggestion list would leave the dropdown empty with no
    // explanation of why it isn't appearing.
    if (classification.status === 'duplicate') {
      return classification;
    }
    // For non-duplicate inputs, only surface the "Add custom domain"
    // affordance when the query LOOKS like an attempted domain entry —
    // i.e. it contains a dot, or is prefixed with a scheme. Pure name
    // searches like "now", "knowledge", "ham" otherwise surface a
    // disabled "Add now" row with a red "Enter a valid domain or URL"
    // caption that reads as noise to a user who's only trying to find
    // an existing source by name. Mirrors the Library "Add source"
    // search UX.
    const looksDomainLike = trimmed.includes('.') || /^https?:\/\//i.test(trimmed);
    if (!looksDomainLike) return null;
    // Skip the custom-domain row when the typed value resolves to a known
    // source — the user can pick it from the regular results list instead.
    if (
      classification.status === 'valid' &&
      allSources.some((s) => s.domain === classification.domain)
    ) {
      return null;
    }
    return classification;
  }, [query, excludedSet, allSources, blockedHosts]);

  const handleSingleSelect = useCallback(
    (domain: string) => {
      onSelectSource(domain);
      inputRef.current?.blur();
      clearAndClose();
    },
    [onSelectSource, inputRef, clearAndClose]
  );

  const handleRowSelect = useCallback(
    (domain: string) => {
      if (multiSelectEnabled) {
        togglePending(domain);
        return;
      }
      handleSingleSelect(domain);
    },
    [multiSelectEnabled, togglePending, handleSingleSelect]
  );

  // The dropdown only renders once the user has typed at least
  // ``MIN_QUERY_LENGTH`` characters. Focusing the input on its own no
  // longer surfaces an unfiltered catalog view — the user must express
  // a query first. See ``useFilteredSources`` for the matching gate.
  const meetsQueryThreshold = query.trim().length >= MIN_QUERY_LENGTH;
  const showDropdown =
    isOpen &&
    meetsQueryThreshold &&
    (filteredResults.length > 0 || customDomainInfo !== null);

  return (
    <SearchDropdownLayout
      containerRef={containerRef}
      inputRef={inputRef}
      query={query}
      onQueryChange={setQuery}
      onOpen={() => setIsOpen(true)}
      placeholder={effectivePlaceholder}
      autoFocus={autoFocus}
      className={className}
      inputTestId="source-search-input"
      showDropdown={showDropdown}
      // Escalate the panel border + shadow: this dropdown overlays the
      // curated "added sources" list in the create-collection / customise
      // wizard, and at the default ``border border-border`` weight the two
      // surfaces blend visually. ``border-2 border-border-strong`` doubles
      // the stroke and shifts to the design system's stronger border
      // token, while ``shadow-xl`` adds enough elevation to read as a
      // floating layer above the list behind it.
      panelClassName="border-2 border-border-strong shadow-xl"
    >
      <div className="flex w-full flex-col">
        {/* Sticky cap-counter bar. Lives OUTSIDE the scrollable
            ``h-72`` viewport so it never gets pushed off-screen by a
            long results list, and so it doesn't steal vertical space
            from the rows. Renders only when the parent provides a cap
            AND multi-select is enabled — single-select surfaces never
            had a cap mental model. */}
        {cap !== undefined && (
          <SourceSearchCapBar
            cap={cap}
            remaining={remaining ?? 0}
            showError={showCapError}
          />
        )}
        <div className="h-72 w-full overflow-hidden">
          {customDomainInfo && (
            <CustomDomainRow
              domain={customDomainInfo.domain}
              status={customDomainInfo.status}
              onSelect={handleSingleSelect}
              rightSlot={
                multiSelectEnabled && filteredResults.length > 0 ? (
                  <SourceSearchMultiSelectControls
                    allVisibleSelected={allVisibleSelected}
                    canSelectAllVisible={canSelectAllVisible}
                    visibleCount={filteredResults.length}
                    pendingCount={pending.size}
                    onSelectAll={handleSelectAllVisible}
                    onClear={handleClearPending}
                  />
                ) : undefined
              }
            />
          )}
          <VList count={filteredResults.length} className="w-full">
            {(index) => {
              const source = filteredResults[index]!;
              const isPending = pending.has(source.domain);

              return (
                <button
                  key={source.domain}
                  type="button"
                  aria-pressed={multiSelectEnabled ? isPending : undefined}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleRowSelect(source.domain);
                  }}
                  className={cn(
                    'flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors duration-150 hover:bg-surface-page',
                    isPending && 'bg-surface-page'
                  )}
                >
                  {multiSelectEnabled && (
                    <span
                      aria-hidden="true"
                      className={cn(
                        'flex size-4 shrink-0 items-center justify-center rounded-sm border border-border-selected',
                        isPending && 'bg-background-primary text-text-primary-invert'
                      )}
                    >
                      {isPending && <Check className="size-3" aria-hidden="true" />}
                    </span>
                  )}
                  <div className="flex size-6 shrink-0 items-center justify-center">
                    <FaviconImage src={getFaviconUrl(`https://${source.domain}`)} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <TypographyP2 className="truncate">{source.label}</TypographyP2>
                    <TypographyCaption className="truncate text-text-tertiary">
                      {source.domain}
                    </TypographyCaption>
                  </div>
                </button>
              );
            }}
          </VList>
        </div>
        {multiSelectEnabled && pending.size > 0 && (
          <div className="flex items-center justify-between gap-3 border-t border-border bg-surface px-3 py-2">
            <TypographyCaption className="text-text-secondary">
              <FormattedMessage
                defaultMessage="{count, plural, one {# selected} other {# selected}}"
                id="0s8piyAuOW"
                description="Count of currently pending source selections in the multi-select dropdown"
                values={{ count: pending.size }}
              />
            </TypographyCaption>
            <Button
              type="button"
              size="xs"
              onMouseDown={(e) => {
                e.preventDefault();
                handleSubmitPending();
              }}
              data-testid="source-search-add-selected"
            >
              <FormattedMessage
                defaultMessage="{count, plural, one {Add # source} other {Add # sources}}"
                id="VeKRv/7Fz0"
                description="Button to commit the multi-select source selections"
                values={{ count: pending.size }}
              />
            </Button>
          </div>
        )}
      </div>
    </SearchDropdownLayout>
  );
};
